import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { AGENT_AUTH_USER_CODE_LENGTH } from "./protocol";

/**
 * What the claim page may say about a ceremony, to a signed-in human holding the
 * claim-attempt token from the URL.
 *
 * `email_mismatch` is a REFUSAL that carries no detail. The token travels in a
 * URL — a chat message, a terminal scrollback, a browser history — so anyone
 * signed in to Nexus can reach this endpoint with someone else's token, and
 * answering "this ceremony is for alice@acme.com" would turn an intercepted link
 * into an address-disclosure oracle. The page says only that this is not the
 * account the agent asked for.
 */
export const AgentAuthClaimStatus = {
  Ready: "ready",
  EmailMismatch: "email_mismatch",
  Expired: "expired",
  AlreadyClaimed: "already_claimed"
} as const;

export const AgentAuthClaimStatusSchema = z.enum([
  AgentAuthClaimStatus.Ready,
  AgentAuthClaimStatus.EmailMismatch,
  AgentAuthClaimStatus.Expired,
  AgentAuthClaimStatus.AlreadyClaimed
]);

const AgentAuthClaimViewSchema = z.object({
  status: AgentAuthClaimStatusSchema,
  /**
   * The label the AGENT supplied for itself. Attacker-controlled text shown to a
   * human — rendered as text, never as markup, and never authorization input.
   */
  agentLabel: z.string().nullable(),
  /** Exactly the scopes the minted credential will carry, for display. */
  scopes: z.array(z.string()),
  /** When the ceremony window closes. */
  expiresAt: z.string()
});

const AgentAuthClaimConfirmationSchema = z.object({
  /** The label echoed back, so the confirmation names what was authorized. */
  agentLabel: z.string().nullable(),
  scopes: z.array(z.string())
});

/**
 * ZAgentAuth — the console's half of the auth.md claim ceremony (NEX-4203).
 *
 * These two routes are the ONLY agent-auth surface the browser touches, and the
 * only one behind a Clerk session. Everything else in the protocol —
 * registration, the token endpoint, revocation, the discovery documents — is an
 * unauthenticated machine surface served under its own controllers and its own
 * wire format, so it is deliberately absent from this contract: an OAuth token
 * response is not a `{ success, data }` envelope and must never be wrapped in one.
 */
export const ZAgentAuth = {
  /**
   * GET /api/agent-auth/claim
   * Resolve a claim attempt for the signed-in user, so the page can show what is
   * being authorized before anything is granted.
   */
  GetClaim: {
    method: "GET" as const,
    path: "/api/agent-auth/claim",
    Query: z.object({
      claimAttemptToken: z.string().min(8).max(128)
    }),
    Response: ApiSuccessSchema(AgentAuthClaimViewSchema)
  },

  /**
   * POST /api/agent-auth/claim/confirm
   * Complete the ceremony: bind the registration to the signed-in user and mint
   * the credential the agent is polling for.
   */
  ConfirmClaim: {
    method: "POST" as const,
    path: "/api/agent-auth/claim/confirm",
    Body: z.object({
      claimAttemptToken: z.string().min(8).max(128),
      userCode: z
        .string()
        .length(AGENT_AUTH_USER_CODE_LENGTH)
        .regex(/^[0-9]+$/, "The code is six digits")
    }),
    Response: ApiSuccessSchema(AgentAuthClaimConfirmationSchema)
  }
} as const;
